import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { ReplaySubject } from 'rxjs/ReplaySubject';
import { Observable } from 'rxjs/Rx';
import { User } from '../models';
import { ApiService } from './api.service';
import { SessionService } from './session.service';


@Injectable()
export class AuthService {
    private currentUserSubject = new BehaviorSubject<User>(new User());
    public currentUser = this.currentUserSubject.asObservable().distinctUntilChanged();

    private authenticatedUserSubject = new BehaviorSubject<User>(new User());
    public authenticatedUser = this.authenticatedUserSubject.asObservable().distinctUntilChanged();

    private isAuthenticatedSubject = new ReplaySubject<boolean>(1);
    public isAuthenticated = this.isAuthenticatedSubject.asObservable();

    public isLoginByAuthToken: boolean = false;
    public isSessionPopulated: boolean = false;

    constructor(
        private router: Router,
        private apiService: ApiService,
        private sessionService: SessionService
    ) {
        this.isAuthenticatedSubject.next(false);
    }

    populate(): Observable<User> {
        if (this.isSessionPopulated) return this.authenticatedUser;
        let token = this.sessionService.getSessionToken();
        if (token) {
            this.isSessionPopulated = true;
            return this.validateToken(token);
        } else {
            // Remove any potential remnants of previous auth states
            return new Observable(observer => {
                this.purgeAuth();
                observer.next(null);
                observer.complete();
            });
        }
    }

    login(credentials): Observable<User> {
        console.log("login: Start");
        if (credentials.AccessCode && credentials.AccessCode.length > 0) {
            return this.apiService.post('/customer/authenticate', credentials).map(
                data => {
                    console.log("login: Successful");
                    this.setAuth(data);
                    return data;
                }
            )
        } else {
            return this.apiService.post('/authenticate', credentials).map(
                data => {
                    console.log("login: Successful");
                    this.setAuth(data);
                    return data;
                }
            )
        }
    }

    validateSsoToken(token: string): Observable<User> {
        let params: any = { ot: token };
        return this.apiService.get('/authenticate/sso', params).map(result => {
            if (result && result.User) {
                this.setAuth(result.User);
                return result.User;
            }
            return null;
        });
    }

    validateToken(token: string): Observable<User> {
        let params: any = { session_token: token };
        return this.apiService.get('/authenticate/validate', params).map(result => {
            if (result && result.User) {
                this.setAuth(result.User);
                this.sessionService.setSessionUser(result.User);
                return result.User;
            }
            return null;
        });
    }

    extendAuthSession(): Observable<any> {
        return this.apiService.get("/authenticate/extend_session").map(
            (result) => {
                console.log("extend_session");
                return result;
            }
        );
    }

    ssoLogin(token: string): Observable<User> {
        this.isLoginByAuthToken = true;
        return this.validateSsoToken(token);
    }

    logoff(isTimeOut: boolean = false) {
        let logout_url: string = '/login';
        var user = this.getAuth();
        this.purgeAuth();
        if (isTimeOut && user && user.sso_signin_url && user.sso_signin_url.length > 0) {
            window.location.href = user.sso_signin_url;
        } else if (user && user.sso_signout_url && user.sso_signout_url.length > 0) {
            window.location.href = user.sso_signout_url;
        } else if (user && user.logout_url && user.logout_url.length > 0) {
            window.location.href = user.logout_url;
        } else {
            this.router.navigateByUrl(logout_url);
        }
    }

    setAuth(user: User) {
        this.sessionService.startUserIdleService();
        // Save JWT sent from server in localstorage
        if (user.session_token) this.sessionService.saveSessionToken(user.session_token);
        // Set current user data into observable
        //if (user.home_page == undefined) user.home_page = "/usc-store";
        this.sessionService.log("auth user", user);
        this.authenticatedUserSubject.next(user);
        this.currentUserSubject.next(user);
        this.isAuthenticatedSubject.next(true);
        // Set isAuthenticated to true
        // this.isAuthenticatedSubject.next(true);
    }

    getAuth(): User {
        return this.authenticatedUserSubject.value;
    }

    getActive(): User {
        return this.currentUserSubject.value;
    }

    purgeAuth() {
        // Remove JWT from localstorage
        this.sessionService.destroySessionToken();
        // Set current user to an empty object
        if (this.currentUserSubject.value.ID) {
            this.currentUserSubject.next(new User());
            this.authenticatedUserSubject.next(new User());
            // Set auth status to false
            this.isAuthenticatedSubject.next(false);
            this.isLoginByAuthToken = false;
        }
    }

    setIsAuthenticated(bValue: boolean) {
        this.isAuthenticatedSubject.next(bValue);
    }

    validateAuthentication() {
        if (!this.sessionService.getSessionToken()) {
            this.logoff();
        }
    }

    isUSCellRetail() {
        var user = this.getAuth();
        return (user && user.client_code && user.client_code.indexOf("USCELL-RETAIL") >= 0);
    }

    isUSCellCorp() {
        var user = this.getAuth();
        return (user && user.client_code && user.client_code.indexOf("USCELL-RETAIL-CORP") >= 0);
    }

    isUSCellAgent() {
        var user = this.getAuth();
        return (user && user.client_code && user.client_code.indexOf("USCELL-RETAIL-AGENT") >= 0);
    }

    isUSCellCampaign() {
        var user = this.getAuth();
        return (user && user.client_code && user.client_code.indexOf("USCELL-INTERNAL") >= 0);
    }
}


// WEBPACK FOOTER //
// C:/Azure/Source/Workspaces/SalesNOW/SalesSOLO/WebSites/SalesSOLO.Web.Angular.telesales/src/app/services/auth.service.ts